import { Text } from "components";
import React from "react";
import { format } from "date-fns";
import { E_AssistantActions, T_AssistantLogs } from "types";
import {
  ActivityItemLeft,
  ActivityItemStyle,
  DraftTextHightlight,
  EmailTextHightlight,
  MeetingTextHightlight,
} from "../styles";
import { FontFamily } from "assets/theme";

type Props = {
  inspecting?: boolean;
  item: T_AssistantLogs;
  onSelect?: (item: T_AssistantLogs) => void;
};
export const ActivityItem: React.FC<Props> = ({
  item,
  onSelect,
  inspecting,
}) => {
  const { serviceName, actions, category, created, from, subject } = item;

  const { labeled, joined } = getAction(actions);

  const isEmail = serviceName === "email";

  function renderMeetingAction() {
    const [action] = actions;
    switch (action) {
      case E_AssistantActions.MeetingPrepDone: {
        return (
          <div className={ActivityItemLeft}>
            <Text.Body2XS>
              <span className={MeetingTextHightlight}>Meeting prep</span>{" "}
              <b>{category ? ": " + category : ""}</b> is ready and waiting for
              your review
            </Text.Body2XS>
          </div>
        );
      }

      case E_AssistantActions.MeetingPrepScheduled: {
        return (
          <div className={ActivityItemLeft}>
            <Text.Body2XS>
              <span className={MeetingTextHightlight}>Meeting prep</span>{" "}
              scheduled for <b>{category}</b>
            </Text.Body2XS>
          </div>
        );
      }

      case E_AssistantActions.MeetingPrepSkipped: {
        return (
          <div className={ActivityItemLeft}>
            <Text.Body2XS>
              <span className={MeetingTextHightlight}>Meeting prep</span>{" "}
              canceled for <b>{category}</b>
            </Text.Body2XS>
          </div>
        );
      }
      case E_AssistantActions.MeetingPrepStarted: {
        return (
          <div className={ActivityItemLeft}>
            <Text.Body2XS fontFamily={FontFamily.RoobertLight}>
              <span className={MeetingTextHightlight}>Meeting prep</span>{" "}
              started for <b>{category}</b>
            </Text.Body2XS>
          </div>
        );
      }
      default:
        return (
          <div className={ActivityItemLeft}>
            <Text.Body2XS fontFamily={FontFamily.RoobertLight}>
              {action}
            </Text.Body2XS>
          </div>
        );
    }
  }

  const renderEmailAction = () => {
    const [action] = actions;
    if (action === E_AssistantActions.EmailDrafted) {
      return (
        <div className={ActivityItemLeft}>
          <Text.Body2XS>
            Email response <span className={DraftTextHightlight}>Draft</span>{" "}
            written for you: <b>{subject}</b>
          </Text.Body2XS>
        </div>
      );
    }
    return (
      <div className={ActivityItemLeft}>
        <Text.Body2XS fontFamily={FontFamily.RoobertLight}>
          <span className={EmailTextHightlight}>Email</span> from{" "}
          {extractFrom(from || "") || from} {labeled} <b>{category}</b>{" "}
          {joined ? (actions.length > 2 ? ", " : " and") : ""}{" "}
          <b>{joined || ""}</b>
        </Text.Body2XS>
      </div>
    );
  };

  return (
    <div
      className={ActivityItemStyle}
      style={{ display: inspecting ? "block" : "flex" }}
      onClick={() => {
        onSelect?.(item);
      }}
    >
      {isEmail ? renderEmailAction() : renderMeetingAction()}
      <Text.Body2XS color="darkGrey">
        {format(new Date(created || Date.now()), "h:mm a")}
      </Text.Body2XS>
    </div>
  );
};

function mapAction(action: E_AssistantActions) {
  switch (action) {
    case E_AssistantActions.Archived:
      return "archived";
    case E_AssistantActions.CalendarEventCreated:
      return "created a calendar event";
    case E_AssistantActions.Urgent:
      return "marked as urgent";
    case E_AssistantActions.EmailLabeled:
      return "labeled as";
    case E_AssistantActions.PersonSearch:
      return "searched for a person";
    case E_AssistantActions.MeetingPrepDone:
      return "searched for a person";
    default:
      return action;
  }
}

function getAction(actions: E_AssistantActions[]) {
  const [labeled, ...rest] = actions;
  let joined = rest.map((item) => mapAction(item)).join(", ");

  // add 'and' before the last action
  const lastComma = joined.lastIndexOf(",");
  if (lastComma !== -1) {
    joined = joined.slice(0, lastComma) + " and" + joined.slice(lastComma + 1);
  }
  return {
    labeled: mapAction(labeled),
    joined,
  };
}

function extractFrom(from: string) {
  const parts = from.split("<");
  return parts[0];
}
